import React from "react";
import { Users, Smartphone, Phone } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SectionHeading } from "./SectionHeading";
import {
  WHATSAPP_NUMBERS,
  WHATSAPP_COMMUNITY_LINK,
  INSTAGRAM_LINK,
  buildWhatsAppLink,
  generalEnquireMessage,
} from "@/lib/whatsapp";

// Unified list of ways to reach us on WhatsApp — both phone numbers.
const contacts = WHATSAPP_NUMBERS.map((wa) => ({
  key: wa.number,
  icon: Phone,
  label: wa.label,
  sub: "WhatsApp Enquiry Line",
  active: wa.active,
  href: buildWhatsAppLink(generalEnquireMessage(), wa.number),
}));

export const EnquirySection = () => (
  <section id="enquire" className="relative py-20 sm:py-28 bg-background">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading
        eyebrow="Get In Touch"
        title="Enquire With Us"
        subtitle="Reach out directly — no forms, no waiting. Just tap and chat with our team on WhatsApp."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 max-w-3xl mx-auto">
        {contacts.map((c, idx) => (
          <div key={c.key} className="card-lux rounded-2xl p-6 flex flex-col h-full" data-testid={`whatsapp-number-card-${idx}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center">
                <c.icon className="h-5 w-5 text-primary" />
              </div>
              {c.active && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-whatsapp">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-whatsapp opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-whatsapp" />
                  </span>
                  Always Active
                </span>
              )}
            </div>
            <p className="font-heading text-xl font-semibold text-foreground tracking-wide">{c.label}</p>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{c.sub}</p>
            <a
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "whatsapp" }), "mt-5 w-full")}
              data-testid={`whatsapp-chat-btn-${idx}`}
            >
              <i className="fa-brands fa-whatsapp" /> Chat Now
            </a>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="card-lux rounded-2xl p-6 flex flex-col h-full" data-testid="community-card">
          <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-foreground">Join Our WhatsApp Community</h3>
          <p className="text-sm text-muted-foreground mt-2 flex-1 leading-relaxed">
            Get first access to new arrivals, offers &amp; updates from Vini Gems And Jewellers.
          </p>
          <a
            href={WHATSAPP_COMMUNITY_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: "outline" }), "mt-5 w-full")}
            data-testid="join-community-btn"
          >
            Join Community
          </a>
        </div>

        <div className="card-lux rounded-2xl p-6 flex flex-col h-full" data-testid="instagram-card">
          <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <i className="fa-brands fa-instagram text-primary text-lg" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-foreground">Follow @vini_jewelleryinfo</h3>
          <p className="text-sm text-muted-foreground mt-2 flex-1 leading-relaxed">
            See our latest designs, reels &amp; behind-the-scenes on Instagram.
          </p>
          <a
            href={INSTAGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: "outline" }), "mt-5 w-full")}
            data-testid="instagram-follow-btn"
          >
            Follow on Instagram
          </a>
        </div>

        <div className="card-lux rounded-2xl p-6 flex flex-col h-full opacity-90" data-testid="app-card">
          <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Smartphone className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-heading text-lg font-semibold text-foreground">Mobile App</h3>
          <p className="text-sm text-muted-foreground mt-2 flex-1 leading-relaxed">
            Our dedicated mobile app is on its way. Download link coming soon.
          </p>
          <span
            className={cn(buttonVariants({ variant: "secondary" }), "mt-5 w-full cursor-not-allowed opacity-70")}
            data-testid="app-coming-soon-btn"
          >
            Coming Soon
          </span>
        </div>
      </div>
    </div>
  </section>
);
